import { Dropdown } from "flowbite";
import {
  DataTable,
  exportCSV,
  exportJSON,
  exportTXT,
  exportSQL,
} from "simple-datatables";

// Función reutilizable para crear DataTables con funciones de exportación
function createDataTableWithExport(tableSelector, options = {}) {
  const tableElement = document.querySelector(tableSelector);
  if (!tableElement || typeof DataTable === "undefined") {
    console.warn(
      `Tabla ${tableSelector} no encontrada o DataTable no disponible`
    );
    return null;
  }

  // Obtener el ID de la tabla para generar IDs únicos
  const tableId = tableElement.id || tableSelector.replace("#", "");
  const exportButtonId = `exportDropdownButton-${tableId}`;
  const exportDropdownId = `exportDropdown-${tableId}`;
  const exportCsvId = `export-csv-${tableId}`;
  const exportJsonId = `export-json-${tableId}`;
  const exportTxtId = `export-txt-${tableId}`;
  const exportSqlId = `export-sql-${tableId}`;

  // Función personalizada para exportar CSV con separadores
  const exportCustomCSV = function (dataTable, userOptions = {}) {
    const clonedUserOptions = {
      ...userOptions,
    };
    clonedUserOptions.download = false;

    // Si no se especifican columnas, excluir la última por defecto
    if (!clonedUserOptions.columns) {
      const totalColumns = dataTable.data.headings.length;
      const columnsToExport = [];
      for (let i = 0; i < totalColumns - 1; i++) {
        columnsToExport.push(i);
      }
      clonedUserOptions.columns = columnsToExport;
    }

    const csv = exportCSV(dataTable, clonedUserOptions);

    if (!csv) {
      return false;
    }

    const defaults = {
      download: true,
      lineDelimiter: "\n",
      columnDelimiter: ";",
    };
    const exportOptions = {
      ...defaults,
      ...clonedUserOptions,
    };

    // Calcular separadores basado en las columnas que se van a exportar
    const columnsCount = clonedUserOptions.columns
      ? clonedUserOptions.columns.length
      : dataTable.data.headings.length - 1;
    const separatorRow = Array(columnsCount).fill("+").join("+");

    const str =
      separatorRow +
      exportOptions.lineDelimiter +
      csv +
      exportOptions.lineDelimiter +
      separatorRow;

    if (userOptions.download) {
      const link = document.createElement("a");
      link.href = encodeURI("data:text/csv;charset=utf-8," + str);
      link.download =
        (exportOptions.filename || `datatable_export_${tableId}`) + ".txt";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    return str;
  };

  // Configuración por defecto del DataTable
  const defaultTableOptions = {
    labels: {
      placeholder: "Buscar...",
      perPage: "registros por página",
      noRows: "No se encontraron registros",
      info: "Mostrando {start} a {end} de {rows} registros",
      infoEmpty: "Mostrando 0 a 0 de 0 registros",
      infoFiltered: "(filtrado de {rows} registros totales)",
      infoPostFix: "",
      search: "Buscar:",
      searchTitle: "Buscar en la tabla",
      next: "Siguiente",
      prev: "Anterior",
      first: "Primero",
      last: "Último",
    },
    template: (templateOptions, dom) =>
      "<div class='" +
      templateOptions.classes.top +
      "'>" +
      "<div class='flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-3 rtl:space-x-reverse w-full sm:w-auto'>" +
      (templateOptions.paging && templateOptions.perPageSelect
        ? "<div class='" +
          templateOptions.classes.dropdown +
          "'>" +
          "<label>" +
          "<select class='" +
          templateOptions.classes.selector +
          "'></select> " +
          templateOptions.labels.perPage +
          "</label>" +
          "</div>"
        : "") +
      "<button id='" +
      exportButtonId +
      "' type='button' class='flex w-full items-center justify-center rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-900 hover:bg-gray-100 hover:text-primary-700 focus:z-10 focus:outline-none focus:ring-4 focus:ring-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white dark:focus:ring-gray-700 sm:w-auto'>" +
      "Exportar como" +
      "<ion-icon name='chevron-down-outline' class='-me-0.5 ms-1.5 h-4 w-4'></ion-icon>" +
      "</button>" +
      "<div id='" +
      exportDropdownId +
      "' class='z-10 hidden w-52 divide-y divide-gray-100 rounded-lg bg-white shadow-sm dark:bg-gray-700' data-popper-placement='bottom'>" +
      "<ul class='p-2 text-left text-sm font-medium text-gray-500 dark:text-gray-400' aria-labelledby='" +
      exportButtonId +
      "'>" +
      "<li>" +
      "<button id='" +
      exportCsvId +
      "' class='group inline-flex w-full items-center rounded-md px-3 py-2 text-sm text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-600 dark:hover:text-white'>" +
      "<ion-icon name='document-text-outline' class='me-1.5 h-4 w-4 text-gray-400 group-hover:text-gray-900 dark:text-gray-400 dark:group-hover:text-white'></ion-icon>" +
      "<span>Exportar CSV</span>" +
      "</button>" +
      "</li>" +
      "<li>" +
      "<button id='" +
      exportJsonId +
      "' class='group inline-flex w-full items-center rounded-md px-3 py-2 text-sm text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-600 dark:hover:text-white'>" +
      "<ion-icon name='code-outline' class='me-1.5 h-4 w-4 text-gray-400 group-hover:text-gray-900 dark:text-gray-400 dark:group-hover:text-white'></ion-icon>" +
      "<span>Exportar JSON</span>" +
      "</button>" +
      "</li>" +
      "<li>" +
      "<button id='" +
      exportTxtId +
      "' class='group inline-flex w-full items-center rounded-md px-3 py-2 text-sm text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-600 dark:hover:text-white'>" +
      "<ion-icon name='document-outline' class='me-1.5 h-4 w-4 text-gray-400 group-hover:text-gray-900 dark:text-gray-400 dark:group-hover:text-white'></ion-icon>" +
      "<span>Exportar TXT</span>" +
      "</button>" +
      "</li>" +
      "<li>" +
      "<button id='" +
      exportSqlId +
      "' class='group inline-flex w-full items-center rounded-md px-3 py-2 text-sm text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-600 dark:hover:text-white'>" +
      "<ion-icon name='server-outline' class='me-1.5 h-4 w-4 text-gray-400 group-hover:text-gray-900 dark:text-gray-400 dark:group-hover:text-white'></ion-icon>" +
      "<span>Exportar SQL</span>" +
      "</button>" +
      "</li>" +
      "</ul>" +
      "</div>" +
      "</div>" +
      (templateOptions.searchable
        ? "<div class='" +
          templateOptions.classes.search +
          "'>" +
          "<input class='" +
          templateOptions.classes.input +
          "' placeholder='" +
          templateOptions.labels.placeholder +
          "' type='search' title='" +
          templateOptions.labels.searchTitle +
          "'" +
          (dom.id ? " aria-controls='" + dom.id + "'" : "") +
          ">" +
          "</div>"
        : "") +
      "</div>" +
      "<div class='" +
      templateOptions.classes.container +
      "'" +
      (templateOptions.scrollY.length
        ? " style='height: " + templateOptions.scrollY + "; overflow-Y: auto;'"
        : "") +
      "></div>" +
      "<div class='" +
      templateOptions.classes.bottom +
      "'>" +
      (templateOptions.paging
        ? "<div class='" + templateOptions.classes.info + "'></div>"
        : "") +
      "<nav class='" +
      templateOptions.classes.pagination +
      "'></nav>" +
      "</div>",
  };

  // Combinar opciones por defecto con las opciones del usuario
  const finalOptions = {
    ...defaultTableOptions,
    ...options,
  };

  // Crear la tabla
  const table = new DataTable(tableSelector, finalOptions);

  // Configurar dropdown de exportación
  const exportButton = document.getElementById(exportButtonId);
  const exportDropdownEl = document.getElementById(exportDropdownId);

  if (exportButton && exportDropdownEl) {
    const dropdown = new Dropdown(exportDropdownEl, exportButton);

    // Función helper para obtener las columnas excluyendo la última (acciones)
    const getColumnsToExport = () => {
      const totalColumns = table.data.headings.length;
      const columnsToExport = [];

      // Incluir todas las columnas excepto la última
      for (let i = 0; i < totalColumns - 1; i++) {
        columnsToExport.push(i);
      }

      return columnsToExport;
    };

    // Event listeners para los botones de exportación
    document.getElementById(exportCsvId)?.addEventListener("click", () => {
      exportCSV(table, {
        download: true,
        lineDelimiter: "\n",
        columnDelimiter: ";",
        filename: `export_${tableId}_csv`,
        columns: getColumnsToExport(),
      });
    });

    document.getElementById(exportSqlId)?.addEventListener("click", () => {
      exportSQL(table, {
        download: true,
        tableName: `export_table_${tableId}`,
        filename: `export_${tableId}_sql`,
        columns: getColumnsToExport(),
      });
    });

    document.getElementById(exportTxtId)?.addEventListener("click", () => {
      exportTXT(table, {
        download: true,
        filename: `export_${tableId}_txt`,
        columns: getColumnsToExport(),
      });
    });

    document.getElementById(exportJsonId)?.addEventListener("click", () => {
      exportJSON(table, {
        download: true,
        space: 3,
        filename: `export_${tableId}_json`,
        columns: getColumnsToExport(),
      });
    });
  }

  return table;
}

// Ejemplos de uso:
// Para tu tabla original:
if (document.getElementById("export-table")) {
  createDataTableWithExport("#export-table");
}

// Para otras tablas:
// createDataTableWithExport("#otra-tabla");
// createDataTableWithExport("#tabla-usuarios", {
//   labels: { placeholder: "Buscar usuarios..." }
// });

// Exportar la función para poder usarla en otros archivos
// window.createDataTableWithExport = createDataTableWithExport;
